import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLang } from '../context/LangContext';
import { IMAGES, SPECIALS } from '../data/menuData';
import Footer from '../components/Footer';
import './Home.css';

function scrollToId(id) {
  const el = document.getElementById(id);
  if (!el) return;
  const navH = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--nav-h')) || 64;
  window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - navH, behavior: 'smooth' });
}

function StarRating({ stars }) {
  return (
    <div className="stars">
      {[1,2,3,4,5].map(i => {
        const full = i <= Math.floor(stars);
        const half = !full && i - 0.5 <= stars;
        return (
          <span key={i} className={full ? '' : half ? 'star-half' : 'star-dim'}>★</span>
        );
      })}
    </div>
  );
}

export default function Home() {
  const { t } = useLang();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (location.state?.scrollTo) {
      setTimeout(() => scrollToId(location.state.scrollTo), 100);
    }
  }, [location.state]);

  return (
    <div>
      {/* ── HERO ── */}
      <section className="hero">
        <div className="hero-photo" style={{ backgroundImage: `url(${IMAGES.hero})` }} />
        <div className="hero-overlay" />
        <div className="hero-content">
          <div className="hero-eyebrow">{t('hero.eyebrow')}</div>
          <h1>
            {t('hero.h1.line1')}<br />
            <em>{t('hero.h1.em1')}</em><br />
            <em>{t('hero.h1.em2')}</em>
          </h1>
          <p className="hero-desc">{t('hero.desc')}</p>
          <div className="hero-actions">
            <button className="btn-primary" onClick={() => navigate('/menu')}>{t('hero.cta1')}</button>
            <button className="btn-outline" onClick={() => navigate('/reservations')}>{t('hero.cta2')}</button>
          </div>
        </div>
        <div className="hero-badge">
          <div className="hero-badge-label">{t('hero.badge.label')}</div>
          <div className="hero-badge-time">11h30 – 22h30</div>
          <div className="hero-badge-sub">{t('hero.badge.sub')}</div>
        </div>
      </section>

      {/* ── FEATURES ── */}
      <div className="features">
        {[
          { icon: '🌿', titleKey: 'feat1.title', descKey: 'feat1.desc' },
          { icon: '🔥', titleKey: 'feat2.title', descKey: 'feat2.desc' },
          { icon: '🥡', titleKey: 'feat3.title', descKey: 'feat3.desc' },
        ].map((f, i) => (
          <div className="feature-item" key={i}>
            <div className="feature-icon">{f.icon}</div>
            <div>
              <div className="feature-title">{t(f.titleKey)}</div>
              <div className="feature-desc">{t(f.descKey)}</div>
            </div>
          </div>
        ))}
      </div>

      {/* ── SPECIALS ── */}
      <section className="specials" id="specials-section">
        <div className="section-header">
          <div>
            <div className="section-label">{t('specials.label')}</div>
            <h2 className="section-title">{t('specials.title')}</h2>
            <p className="specials-sub">{t('specials.sub')}</p>
          </div>
          <button className="view-all" onClick={() => navigate('/menu')}>{t('specials.viewall')}</button>
        </div>
        <div className="specials-grid">
          {SPECIALS.map((s, i) => (
            <div className="scard" key={i}>
              <div className="scard-photo" style={{ backgroundImage: `url(${IMAGES[s.img]})` }} />
              <div className="scard-body">
                <h4>{t(s.nameKey)}</h4>
                <p>{t(s.descKey)}</p>
                <div className="scard-footer">
                  <StarRating stars={s.stars} />
                  <div className="scard-right">
                    <span className="scard-price">{s.price}</span>
                    <button className="scard-add" onClick={() => navigate('/reservations')}>+</button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── PHOTO STRIP ── */}
      <div className="photo-strip">
        {[
          { img: IMAGES.kottu, label: 'Kottu Roti' },
          { img: IMAGES.hoppers, labelKey: 'strip.hoppers' },
          { img: IMAGES.pork, labelKey: 'strip.curries' },
        ].map((s, i) => (
          <div key={i} className="strip-photo" style={{ backgroundImage: `url(${s.img})` }}>
            <div className="strip-overlay">
              <span className="strip-label">{s.labelKey ? t(s.labelKey) : s.label}</span>
            </div>
          </div>
        ))}
      </div>

      {/* ── STORY + MAP ── */}
      <div className="story-map" id="story-section">
        <div className="story-box">
          <div>
            <div className="section-label">{t('about.label')}</div>
            <h3 className="story-title">{t('about.title')}</h3>
            <p>{t('about.p1')}</p>
            <p>{t('about.p2')}</p>
          </div>
          <div className="chef-row">
            <div className="chef-avatar" style={{ backgroundImage: `url(${IMAGES.chef})` }} />
            <div>
              <div className="chef-name">{t('chef.name')}</div>
              <div className="chef-role">{t('chef.role')}</div>
            </div>
          </div>
        </div>
        <div className="map-box">
          <div className="map-bg" style={{ backgroundImage: `url(${IMAGES.map})` }} />
          <div className="map-content">
            <div className="map-pin">📍</div>
            <h3>{t('map.visit')}</h3>
            <p>{t('map.address')}</p>
            <div className="map-hours">
              {t('map.hours').split('\n').map((line, i) => <div key={i}>{line}</div>)}
            </div>
            <button className="btn-directions" onClick={() => navigate('/reservations')}>{t('map.btn')}</button>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
